import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { projectService, Project } from '../services/projectService';
import { organizationService, OrgMember } from '../services/organizationService';
import { projectStore } from '../lib/projectStore';
import '../styles/CreateProjectModal.css';

interface CreateProjectModalProps {
  onClose: () => void;
  onCreated: (project: Project) => void;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ onClose, onCreated }) => {
  const { currentOrg, user } = useAuth();
  const [projectName, setProjectName] = useState('');
  const [memberQuery, setMemberQuery] = useState('');
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<OrgMember[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!currentOrg) return;
    organizationService.getMembersWithProfiles(currentOrg.id)
      .then(setMembers)
      .catch(() => setMembers([]))
      .finally(() => setMembersLoaded(true));
  }, [currentOrg]);

  const selectedIds = useMemo(() => new Set(selectedMembers.map(m => m.user_id)), [selectedMembers]);

  const matches = useMemo(() => {
    const q = memberQuery.trim().toLowerCase();
    if (!q) return [];
    return members.filter(m =>
      !selectedIds.has(m.user_id) &&
      m.user_id !== user?.id &&
      ((m.full_name && m.full_name.toLowerCase().includes(q)) ||
       (m.email && m.email.toLowerCase().includes(q)))
    ).slice(0, 6);
  }, [memberQuery, members, selectedIds, user?.id]);

  const addMember = (member: OrgMember) => {
    setSelectedMembers(prev => [...prev, member]);
    setMemberQuery('');
    setSearchError(null);
  };

  const removeMember = (userId: string) => setSelectedMembers(prev => prev.filter(m => m.user_id !== userId));

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const q = memberQuery.trim().toLowerCase();
    if (!q) return;

    if (matches.length === 1) {
      addMember(matches[0]);
      return;
    }

    const exact = members.find(m =>
      !selectedIds.has(m.user_id) && m.user_id !== user?.id &&
      (m.email?.toLowerCase() === q || m.full_name?.toLowerCase() === q)
    );
    if (exact) {
      addMember(exact);
      return;
    }

    setSearchError(membersLoaded
      ? 'No member with that name or email was found in this organization.'
      : 'Still loading organization members — please try again.');
  };

  const handleGetStarted = async () => {
    if (!projectName.trim()) { alert('Project name is required'); return; }
    setCreating(true);
    try {
      const project = await projectService.createProject(
        projectName.trim(),
        selectedMembers.map(m => m.user_id)
      );
      projectStore.setProjectId(project.id);
      onCreated(project);
    } catch (err) {
      console.error('Error creating project:', err);
      alert('Failed to create project. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="pjm-modal-backdrop" onClick={() => !creating && onClose()}>
      <div className="pjm-modal" onClick={e => e.stopPropagation()}>
        <div className="pjm-modal-header">
          <h2>Create a new project</h2>
          <button className="pjm-close-btn" onClick={onClose} disabled={creating}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div className="pjm-field">
          <label className="pjm-label">Project name <span style={{ color: '#ef4444' }}>*</span></label>
          <input
            className="pjm-input"
            type="text"
            placeholder="e.g. Mobile App Revamp"
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            autoFocus
          />
        </div>

        <div className="pjm-field">
          <label className="pjm-label">Add teammates (search by name or email)</label>
          <div className="pjm-email-input-row" style={{ position: 'relative' }}>
            <input
              className="pjm-input"
              type="text"
              placeholder="Search organization members..."
              value={memberQuery}
              onChange={e => { setMemberQuery(e.target.value); setSearchError(null); }}
              onKeyDown={handleSearchSubmit}
            />
            {memberQuery.trim() && matches.length > 0 && (
              <div className="pjm-member-suggestions">
                {matches.map(m => (
                  <button type="button" key={m.user_id} className="pjm-member-suggestion" onClick={() => addMember(m)}>
                    <span className="pjm-member-name">{m.full_name || m.email}</span>
                    {m.full_name && <span className="pjm-member-email">{m.email}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {searchError && <p className="pjm-search-error">{searchError}</p>}
          {selectedMembers.length > 0 && (
            <div className="pjm-email-chips">
              {selectedMembers.map(m => (
                <span key={m.user_id} className="pjm-email-chip">
                  {m.full_name || m.email}
                  <button type="button" onClick={() => removeMember(m.user_id)} aria-label={`Remove ${m.full_name || m.email}`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="pjm-modal-footer">
          <button className="pjm-get-started-btn" onClick={handleGetStarted} disabled={creating || !projectName.trim()}>
            {creating ? 'Creating...' : 'Get started'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
